import { useAppDispatch } from 'common/stores/redux/hooks';
import {
    DOCUMENT_TRADE_TYPE,
    ORDER_FULFILLMENT_TYPE,
    ORDER_ISSUE,
    ORDER_STATUS,
    OrderIssueAPI,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import { useOrderFulfillmentTypesQuery } from 'common/stores/redux/MetaStore/MetaStoreApi';
import {
    DocumentContact,
    FulfillmentDistributionCenter,
    Order,
    Truck,
} from 'common/types';
import * as Validator from 'common/utils/Validator';
import { DataGridAdvancedFilter } from 'common/types/datagrid';
import _, { difference } from 'lodash';
import { updateFulfillmentDetails as updateFulfillmentDetailsTruck } from 'common/stores/redux/Truck';

export const isValidContact = ({
    typeId,
    name,
    email,
    mobile,
    phone,
    notifications,
}: Partial<DocumentContact>) => {
    if (!typeId) return false;
    if (!name || !Validator.isMax80Characters(name)) return false;
    if (email && !Validator.isEmail(email)) return false;
    if (mobile && !Validator.isPhone(mobile)) return false;
    if (phone && !Validator.isPhone(phone)) return false;
    if (!(email || mobile || phone)) return false;

    if (!mobile && !email && notifications && notifications.length > 0) {
        return false;
    }
    return true;
};

export const useFulfillmentTypeDisplay = (
    id: ORDER_FULFILLMENT_TYPE | string
) => {
    const { data: fulfillmentTypes } = useOrderFulfillmentTypesQuery();
    return fulfillmentTypes?.find(t => t.id === id)?.display;
};

// if true then it is a Regional Ordering workflow
export const canAddFulfillmentDetails = (
    order: Pick<Order, 'tradeTypeId' | 'tradeSubTypeId'>
) =>
    !!(
        (order?.tradeTypeId === DOCUMENT_TRADE_TYPE.PLUMBING ||
            order?.tradeTypeId === DOCUMENT_TRADE_TYPE.HVAC) &&
        order?.tradeSubTypeId
    );

// TODO: This is the new function that should be used. We don't need to dispatch in here, we can just return
// the updated fulfillment and then dispatch to the redux store in the event handler function
export const copyFulfillmentFromTruckToOrder = (order: Order, truck: Truck) => {
    if (canAddFulfillmentDetails(order)) {
        const fulfillmentDetails = {
            fulfillmentTypeId: truck.fulfillmentDetails?.fulfillmentTypeId,
            fulfillmentDistributionCenterId:
                truck.fulfillmentDetails?.distributionCenterId,
            fulfillmentToAddress:
                truck.fulfillmentDetails?.fulfillmentToAddress,
            fulfillmentToCity: truck.fulfillmentDetails?.fulfillmentToCity,
            fulfillmentToRegion: truck.fulfillmentDetails?.fulfillmentToRegion,
            fulfillmentToPostalCode:
                truck.fulfillmentDetails?.fulfillmentToPostalCode,
            fulfillmentTo: truck.fulfillmentDetails?.fulfillmentTo,
            residentialDelivery: truck.fulfillmentDetails?.residentialDelivery,
        };

        return {
            fulfillmentDetails,
            fulfillmentDistributionCenter: truck.fulfillmentDistributionCenter,
        };
    }
};

// TODO: This will eventually be deprecated once we move away from alt.js flux.
export const copyFulfillmentFromTruckToOrderStore = (
    order: Order,
    truck: Truck,
    OrderCreationStoreActions: any
) => {
    if (canAddFulfillmentDetails(order)) {
        const fulfillmentDetails = {
            fulfillmentTypeId: truck.fulfillmentDetails?.fulfillmentTypeId,
            fulfillmentDistributionCenterId:
                truck.fulfillmentDetails?.distributionCenterId,
            fulfillmentToAddress:
                truck.fulfillmentDetails?.fulfillmentToAddress,
            fulfillmentToCity: truck.fulfillmentDetails?.fulfillmentToCity,
            fulfillmentToRegion: truck.fulfillmentDetails?.fulfillmentToRegion,
            fulfillmentToPostalCode:
                truck.fulfillmentDetails?.fulfillmentToPostalCode,
            fulfillmentTo: truck.fulfillmentDetails?.fulfillmentTo,
            residentialDelivery: truck.fulfillmentDetails?.residentialDelivery,
        };
        OrderCreationStoreActions.updateFulfillmentDetails({
            fulfillmentDetails,
            fulfillmentDistributionCenter: truck.fulfillmentDistributionCenter,
        });
    }
};

export const copyFulfillmentFromOrderToTruckStore = (
    order: Order,
    dispatch: ReturnType<typeof useAppDispatch>
) => {
    if (canAddFulfillmentDetails(order)) {
        const fulfillmentDetails = {
            fulfillmentTypeId: order.fulfillmentTypeId,
            distributionCenterId: order.fulfillmentDistributionCenterId,
            fulfillmentToAddress: order.fulfillmentToAddress,
            fulfillmentToCity: order.fulfillmentToCity,
            fulfillmentToRegion: order.fulfillmentToRegion,
            fulfillmentToPostalCode: order.fulfillmentToPostalCode,
            fulfillmentTo: order.fulfillmentTo,
            residentialDelivery: order.residentialDelivery,
        };

        dispatch(
            updateFulfillmentDetailsTruck({
                fulfillmentDetails,
                fulfillmentDistributionCenter: order.vendorDistributionCenter,
            })
        );
    }
};

type getFulfillmentAndDistributionCenterDetailsReturnType = {
    fulfillmentDetails: Partial<Order> | null;
    fulfillmentDistributionCenter: FulfillmentDistributionCenter;
};

export const getFulfillmentAndDistributionCenterDetailsFromOrder = (
    order: Order
): getFulfillmentAndDistributionCenterDetailsReturnType => {
    return {
        fulfillmentDetails: {
            fulfillmentTypeId: order.fulfillmentTypeId,
            fulfillmentDistributionCenterId:
                order.fulfillmentDistributionCenterId,
            fulfillmentToAddress: order.fulfillmentToAddress,
            fulfillmentToCity: order.fulfillmentToCity,
            fulfillmentToRegion: order.fulfillmentToRegion,
            fulfillmentToPostalCode: order.fulfillmentToPostalCode,
            fulfillmentTo: order.fulfillmentTo,
            residentialDelivery: order.residentialDelivery,
        },
        fulfillmentDistributionCenter:
            order.vendorDistributionCenter as FulfillmentDistributionCenter,
    };
};

export const applyFulfillmentAndContactsAcls = (statusId: ORDER_STATUS) => {
    const validOrderStatuses = [
        ORDER_STATUS.CREATED,
        ORDER_STATUS.SUBMITTED,
        ORDER_STATUS.HOLD,
    ];
    return validOrderStatuses.includes(statusId);
};

export interface OrderVendorInfoRow {
    vendorDistributionCenter?: {
        stdAddress: string;
        vendor?: { doingBusinessAs: string };
    };
    vendorBranchName?: string;
    vendorBranchNumber?: number;
    fulfillmentTo?: string;
    fulfillmentToAddress?: string;
}

export const formatOrderVendorInfo = (
    row: OrderVendorInfoRow,
    columnId: string
): string | null => {
    let vendorName = '<NO VENDOR ASSIGNED>';
    if (row.vendorDistributionCenter?.vendor) {
        vendorName = row.vendorDistributionCenter.vendor.doingBusinessAs;
    }
    const vendorDistributionCenter = row.vendorDistributionCenter
        ? `${vendorName} - ${row.vendorBranchName} - ${row.vendorBranchNumber} @ ${row.vendorDistributionCenter.stdAddress}`
        : vendorName;
    switch (columnId) {
        case 'PICKING_UP_AT':
            return vendorDistributionCenter;
        case 'SHIPPING_VIA':
            return vendorDistributionCenter;
        case 'SHIPPING_TO':
            return row.fulfillmentTo
                ? `${row.fulfillmentTo} @ ${row.fulfillmentToAddress}`
                : vendorName;
        default:
            return null;
    }
};

export const formatExceptionReasons = (
    issues?: OrderIssueAPI[],
    issueReasons?: { issueId: ORDER_ISSUE; issueReasonId: string[] }[]
) => {
    if (
        !issueReasons ||
        issueReasons.length === 0 ||
        !issues ||
        issues.length === 0
    )
        return undefined;
    return _.sortBy(
        issueReasons?.filter(issueReason => {
            const issue = issues.find(i => i.id === issueReason.issueId);
            if (issue) {
                const hasReasonDiff = difference(
                    issueReason.issueReasonId,
                    Object.keys(issue.reasons)
                );
                return !(hasReasonDiff?.length > 0);
            }
            return false;
        }),
        'issueId'
    )
        ?.map(issueReason => {
            const _issueReason = issues.find(i => i.id === issueReason.issueId);
            return issueReason.issueReasonId
                ?.map(ir => {
                    return _issueReason?.reasons[ir].display;
                })
                .join(', ');
        })
        .join(', ');
};

/**
 * Calculates which issue reason filters should be removed
 * when issue filters change for orders
 */
export const calculateOrderExceptionChildrenChanges = (
    newFilters: DataGridAdvancedFilter[],
    advancedFilters: {
        issueReasonIds?: {
            term: string;
            form: { id: string; parentId: string };
        }[];
    }
) => {
    const currentIssueIds =
        newFilters?.map(issue => (issue.form ? issue.form.id : issue.id)) || [];

    const updatedIssueReasons = advancedFilters.issueReasonIds?.filter(reason =>
        currentIssueIds.includes(reason.form.parentId)
    );

    const updates: {
        issueReasonIds?: typeof updatedIssueReasons;
    } = {};

    // Only include updates if they're different from current filters
    if (
        JSON.stringify(
            advancedFilters.issueReasonIds?.map(issue => issue.term)
        ) !== JSON.stringify(updatedIssueReasons?.map(reason => reason.term))
    ) {
        updates.issueReasonIds = updatedIssueReasons;
    }

    return updates;
};

export const shouldFulfillmentDrawerOpen = (order: {
    tradeTypeId: string;
    fulfillmentDistributionCenterId: string | number;
}) => {
    if (!order) {
        return false;
    }
    if (order.tradeTypeId === 'ELECTRICAL') {
        return false;
    }
    return !order?.fulfillmentDistributionCenterId;
};
